import type { ListParams } from '../type';
import baseKey from './_baseKey';

/**
 * 스케줄 기준 (주간/월간)
 */
export type ScheduleType = 'weekly' | 'monthly';

/**
 * 시간 기준 (시작/종료)
 */
export type TimeType = 'start' | 'end';

const operatingHoursDomainKey = (
  operatingHoursSubDomains?: (string | number)[]
) => baseKey.domainKey('operatingHours', operatingHoursSubDomains);

/**
 * TO 도메인(TO 세팅, 메인/대체 크루 배정) 관련 쿼리키 제너레이터
 */
const operatingHoursKey = {
  /**
   * @returns TO 도메인 기본 쿼리키
   * @example ['operatingHours']
   */
  domain() {
    return operatingHoursDomainKey();
  },
  /**
   * @returns TO 리스트 도메인 기본 쿼리키
   * @example ['operatingHours', 'list']
   */
  list() {
    return [...operatingHoursDomainKey(), 'list'];
  },
  /**
   * @param listParams 지점 ID를 포함한 리스트 파라미터
   * @returns 지점 ID에 해당하는 전체 TO 리스트 요청 쿼리키
   * @example ['operatingHours', 'list', 'all', {...listParams}]
   */
  listAll(listParams: ListParams) {
    return [...baseKey.listKey(operatingHoursDomainKey(), 'all'), listParams];
  },
  /**
   * @param listParams 지점 ID를 포함한 리스트 파라미터
   * @returns 지점 ID에 해당하는 페이지네이션 방식 TO 리스트 요청 쿼리키
   * @example ['operatingHours', 'list', 'page', {...listParams}]
   */
  listByPage(listParams: ListParams) {
    return [...baseKey.listKey(operatingHoursDomainKey(), 'page'), listParams];
  },
  /**
   * @param listParams 지점 ID를 포함한 리스트 파라미터
   * @returns 지점 ID에 해당하는 무한 스크롤 방식 TO 리스트 요청 쿼리키
   * @example ['operatingHours', 'list', 'scroll', {...listParams}]
   */
  listByScroll(listParams: ListParams) {
    return [
      ...baseKey.listKey(operatingHoursDomainKey(), 'scroll'),
      listParams,
    ];
  },
  /**
   * @returns TO 상세 도메인 기본 쿼리키
   * @example ['operatingHours', 'detail']
   */
  detail() {
    return [...operatingHoursDomainKey(), 'detail'];
  },
  /**
   * @param operatingHoursId TO ID
   * @param scheduleType 스케줄 기준 (주간/월간)
   * @param timeType 시간 기준 (시작/종료)
   * @returns 스케줄 및 시간 기준에 따른 TO 상세 데이터 요청 쿼리키
   * @example ['operatingHours', 'detail', 1, 'weekly', 'start']
   */
  detailById(
    operatingHoursId: number,
    scheduleType: ScheduleType,
    timeType?: TimeType
  ) {
    const coreKey = [
      ...baseKey.itemKey(operatingHoursDomainKey(), 'detail'),
      operatingHoursId,
      scheduleType,
    ];
    return timeType ? [...coreKey, timeType] : coreKey;
  },
  /**
   * @param operatingHoursId TO ID
   * @return TO 간략 데이터 요청 쿼리키
   * @example ['operatingHours', 'brief', 1]
   */
  briefById(operatingHoursId: number) {
    return [
      ...baseKey.itemKey(operatingHoursDomainKey(), 'brief'),
      operatingHoursId,
    ];
  },
  /**
   * @returns TO 대체 크루 도메인 기본 쿼리키
   * @example ['operatingHours', 'substitute']
   */
  substituteDomain() {
    return operatingHoursDomainKey(['substitute']);
  },
  /**
   * @param	operatingHoursId TO ID
   * @param targetDate 대상 날짜
   * @return 대상 TO와 대상 날짜에 해당하는 대체 크루 전체 리스트 요청 쿼리키
   * @example ['operatingHours', 'substitute', 'list', 'all', 1, '2022-08-15']
   */
  substituteListById(operatingHoursId: number, targetDate: string) {
    return [
      ...baseKey.listKey(operatingHoursDomainKey(['substitute']), 'all'),
      operatingHoursId,
      targetDate,
    ];
  },
};

export default Object.freeze(operatingHoursKey);
